import { Component, Injector, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { AbstractComponent, CardService, ConfirmationComponent, FirebaseService, PRIORITY_LABEL, TASK_OUTCOME_LABEL, Task, TaskAttachments, TaskPriorityEnum, TasksService, TaskStatusEnum, UploadTypeEnum, User, UserService, taskTypeLabelKey } from 'lib-trend-core';
import { BehaviorSubject, finalize, takeUntil, tap } from 'rxjs';
import { ConcludeActivityDialogComponent, ConcludeActivityDialogResult } from '../activity-dialogs/conclude-activity-dialog/conclude-activity-dialog.component';
import { PostponeActivityDialogComponent } from '../activity-dialogs/postpone-activity-dialog/postpone-activity-dialog.component';
import { TASK_STATUS_LABEL } from '../task.component';

@Component({
    selector: 'app-task-detail',
    templateUrl: './task-detail.component.html',
    styleUrl: './task-detail.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class TaskDetailComponent extends AbstractComponent {
  private loadingSpinnerSubject = new BehaviorSubject<boolean>(false);
  loadingSpinner$ = this.loadingSpinnerSubject.asObservable();

  public task!: Task;
  public listUser: Array<User> = new Array<User>();

  public file: File;
  public fileLoaded = false;
  public bytes: string;

  tasksStatus = Object.values(TaskStatusEnum).map(v => ({ value: v, label: TASK_STATUS_LABEL[v] }));
  public attachments: Array<TaskAttachments> = [];

  isStarting$ = new BehaviorSubject<boolean>(false);
  isCompleting$ = new BehaviorSubject<boolean>(false);

  breadcrumb = [
    { href: '/task', label: 'task.breadcrumb.tasks' },
    { label: 'task.breadcrumb.detail' },
  ];

  /**
   * The activity's deal is outside the user's visibility (E4 validation N2): concluding and
   * postponing need the deal, so they are locked and the page points to a supervisor.
   */
  public dealHidden = false;

  constructor(
    injector: Injector,
    private taskService: TasksService,
    private userService: UserService,
    private dialog: MatDialog,
    public firebaseService: FirebaseService,
    private cardService: CardService,
  ) {
    super(injector);
  }

  ngOnInit(): void {
    const taskId = this.getParam("id");
    if (taskId) {
      this.getTaskById(taskId);
    }
    this.getListUser();
    this.attachments = this.task?.attachments ?? [];
  }

  private getListUser(): void {
    this.userService.getList().subscribe({
      next: (list: Array<User>) => {
        this.listUser = list;
      },
    })
  }

  getTaskById(id: string): void {
    this.taskService.getById(id).subscribe((task: Task) => {
      this.task = task;
      this.checkDealAccess();
    });
  }

  /**
   * Asks the deal endpoint that applies the visibility rule: it answers 404 for a deal the user may
   * not see (RF-44), and the task's own payload cannot tell — it is not narrowed by visibility.
   */
  private checkDealAccess(): void {
    this.dealHidden = false;
    // A deleted deal answers 404 too, but it is not hidden: the activity is one with no deal (RF-52).
    if (!this.dealId || this.dealRemoved) return;

    this.cardService.getById(this.dealId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({ next: () => (this.dealHidden = false), error: () => (this.dealHidden = true) });
  }

  getOwnerUserName(idUser: unknown): string {
    if (idUser) {
      return this.listUser.filter((user: User) => user._id === idUser)
        .map((user: User) => user.name)
        .reduce((name: string, current: string) => current, '-');
    } else {
      return '-';
    }
  }

  getOwnerUserImage(idUser: unknown): string {
    if (idUser) {
      return this.listUser.filter((user: User) => user._id === idUser)
        .map((user: User) => user.image)
        .reduce((image: string, current: string) => current, '-');
    } else {
      return '-';
    }
  }

  /** Priority and status come from their maps; the activity TYPE from the one shared helper (E4). */
  translateEnuns(value: string) {
    return PRIORITY_LABEL[value as TaskPriorityEnum] ||
      TASK_STATUS_LABEL[value as TaskStatusEnum] || value;
  }

  get typeLabel(): string {
    return taskTypeLabelKey(this.task?.type);
  }

  getStatusLabel(status: string): string {
    return TASK_STATUS_LABEL[status as TaskStatusEnum] || status;
  }

  // ---- E4 (SPEC-2026-10-crm-activities-cadence) ----

  /** The activity points to a deal — live or deleted since; the deal row shows which. */
  get hasDeal(): boolean {
    return !!this.task?.card;
  }

  /**
   * The activity's deal was deleted since (E4 RF-52): the activity is then one with no deal —
   * concluded in one step, postponed freely — and the page says what became of the deal. The
   * task's own read populates the deal even when it is removed, so the mark is on it.
   */
  get dealRemoved(): boolean {
    return this.task?.card?.removed === true;
  }

  /** An activity linked to a live deal is concluded through the conclusion dialog only (RF-08, RF-17). */
  get isDealActivity(): boolean {
    return this.hasDeal && !this.dealRemoved;
  }

  get dealId(): string {
    return `${(this.task?.card as { _id?: unknown })?._id ?? this.task?.card ?? ''}`;
  }

  get dealName(): string {
    return this.task?.card?.name ?? '';
  }

  /** Concluir is offered whether or not the activity was started (RF-12) — never on a deal the user may not see. */
  get canConclude(): boolean {
    return (this.task?.status === TaskStatusEnum.PENDING || this.task?.status === TaskStatusEnum.IN_PROGRESS) && !this.dealHidden;
  }

  /** Only an open activity with a scheduled moment can be postponed (RF-24). */
  get canPostpone(): boolean {
    return this.canConclude && !!this.task?.deadlineAt;
  }

  get outcomeLabel(): string {
    return TASK_OUTCOME_LABEL[this.task?.outcome] ?? 'kanban.taskOutcome.other';
  }

  /** `DD/MM/YYYY` alone for an untimed moment (RF-05). */
  get deadlineFormat(): string {
    return this.task?.untimed === true ? 'DD/MM/YYYY' : 'DD/MM/YYYY - HH:mm';
  }

  get postponements() {
    return this.task?.postponements ?? [];
  }

  get completedByName(): string {
    const by = this.task?.completedBy as { name?: string } | string | undefined;
    if (!by) return '';
    return typeof by === 'string' ? this.getOwnerUserName(by) : (by.name ?? '');
  }

  /**
   * One way to conclude (RF-17, plan AD-03): a deal activity opens the conclusion dialog — outcome,
   * moment, duration, notes, move and next step, one submit; any other activity completes in one
   * step, started or not (RF-12, RF-45).
   */
  conclude(): void {
    if (!this.task?._id) return;

    if (this.isDealActivity) {
      this.dialog.open(ConcludeActivityDialogComponent, {
        data: { task: this.task, card: this.task.card },
        width: '720px',
        disableClose: true,
      })
        .afterClosed()
        .pipe(takeUntil(this.destroy$))
        .subscribe((result: ConcludeActivityDialogResult | undefined) => {
          if (result) this.getTaskById(this.task._id);
        });
      return;
    }

    this.completeTask(this.task._id);
  }

  /** RF-24: a new moment with a reason, counted on the activity. */
  postpone(): void {
    if (!this.task?._id) return;

    this.dialog.open(PostponeActivityDialogComponent, { data: { task: this.task }, width: '560px' })
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((postponed: Task | undefined) => {
        if (postponed) this.getTaskById(this.task._id);
      });
  }

  goToDeal(): void {
    if (this.dealId) this.router.navigate(['/kanban'], { queryParams: { card: this.dealId } });
  }

  onAttachmentsSelect(event: any): void {
    const target = event.target;
    const files: FileList = target.files;
    this.file = files[0];

    let reader = new FileReader();
    reader.onload = async (file) => {
      if (this.file) {
        this.loadingSpinnerSubject.next(true);
        const dateMilisecond = new Date().getTime();
        const filename: string = dateMilisecond.toString();

        this.firebaseService.uploadFile(this.file, filename, UploadTypeEnum.TASK).then((snapshot: { url: string }) => {
          this.loadingSpinnerSubject.next(false);
          const currentAttachments = this.formGroup.get('attachment')?.value ?? [];

          // Check if the file already exists in the attachments list
          const fileExists = this.attachments.some(
            (attachment) => attachment.filename === this.file.name && attachment.size === this.file.size
          );

          if (!fileExists) {
            this.formGroup.patchValue({ attachment: [...currentAttachments, snapshot.url] });

            const newAttachment: TaskAttachments = {
              filename: this.file.name,
              contentType: this.file.type,
              size: this.file.size,
              url: snapshot.url,
            };

            this.attachments = [...this.attachments, newAttachment];
            this.fileLoaded = true;
          } else {
            this.alertService.error(this.t('task.detail.fileAlreadyAttached'));
          }
        })
          .catch((error) => this.alertService.error(this.t('task.detail.uploadError')));
      }
    };
    reader.readAsArrayBuffer(this.file);
  }

  removeAttachment(index: number): void {
    const current: string[] = this.formGroup.get('attachment')?.value ?? [];
    const next = current.filter((_, i) => i !== index);

    this.attachments.splice(index, 1);

    this.formGroup.patchValue({ attachment: next });
    this.formGroup.get('attachment')?.markAsDirty();
  }

  getImageUser(idUser: unknown): string {
    if (idUser) {
      return this.listUser.filter((user: User) => user._id === idUser)
        .map((user: User) => user.image)
        .reduce((image: string, current: string) => current, '-');
    } else {
      return '-';
    }
  }

  removeTask(): void {
    const dialogRef = this.dialog.open(ConfirmationComponent, {
      width: '500px',
    });
    dialogRef.afterClosed().subscribe(result => {
      if (Boolean(result) === true) {
        this.taskService.delete(this.task._id).subscribe({
          next: () => {
            this.router.navigate(['/task']);
            this.alertService.success(this.t('task.detail.taskDeletedSuccessfully'));
          },
          error: err => this.alertService.error(err.error.message)
        })
      }
    });
  }

  goToEditTask(): void {
    this.router.navigate([`/task/${this.task._id}/edit`]);
  }

  startTask(id: string): void {
    if (this.isStarting$.value) return;
    this.isStarting$.next(true);

    const prevTask = { ...this.task };

    this.taskService.startTask(id).pipe(
      tap((serverTask) => {
        this.task = { ...serverTask, status: TaskStatusEnum.IN_PROGRESS, startDate: new Date() };
        this.alertService.success(this.t('task.detail.taskStartedSuccessfully'));
      }),
      finalize(() => this.isStarting$.next(false))
    ).subscribe({
      next: () => {
        this.getTaskById(id);
      },
      error: (err) => {
        this.task = prevTask;
        this.alertService.error(err.error.message || this.t('task.detail.errorStartingTask'));
      }
    });
  }

  /** The single-step conclusion of an activity with no deal (RF-45); the API refuses it for a deal activity (RF-08). */
  completeTask(id: string): void {
    if (this.isCompleting$.value) return;
    this.isCompleting$.next(true);

    const prevTask = { ...this.task };

    this.taskService.completeTask(id).pipe(
      tap((serverTask) => {
        this.task = { ...serverTask, status: TaskStatusEnum.DONE, endDate: new Date() };
        this.alertService.success(this.t('task.detail.taskCompletedSuccessfully'));
      }),
      finalize(() => this.isCompleting$.next(false))
    ).subscribe({
      next: () => {
        this.getTaskById(id);
      },
      error: (err) => {
        this.task = prevTask;
        this.alertService.error(err.error.message || this.t('task.detail.errorStartingTask'));
      }
    });
  }

  formatBytes(bytes: number): string {
    const KB = 1024, MB = KB * 1024, GB = MB * 1024;
    if (bytes >= KB) return `${(bytes / KB).toFixed(2)} KB`;
    return `${bytes} B`;
  }
}
